import { useState, useRef } from "react";
import type { TypographyResult, TypographySuggestion } from "../types";
import { analyzeTypography } from "../lib/api";
import { TypographyEditor } from "./TypographyEditor";

interface Props {
  setError: (e: string | null) => void;
}

export function TypographyMode({ setError }: Props) {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [quoteText, setQuoteText] = useState("");
  const [author, setAuthor] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<TypographyResult | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setImagePreview(dataUrl);

      // Extract base64 without the data URL prefix
      const base64 = dataUrl.split(",")[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
    setError(null);
  };

  const handleAnalyze = async () => {
    if (!imageBase64) {
      setError("Please upload an image first");
      return;
    }

    if (!quoteText.trim()) {
      setError("Please enter a quote");
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const analysisResult = await analyzeTypography(
        imageBase64,
        quoteText.trim(),
        author.trim() || undefined
      );
      setResult(analysisResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clearAll = () => {
    setImagePreview(null);
    setImageBase64(null);
    setQuoteText("");
    setAuthor("");
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-8">
      {/* Upload Section */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Typography Layout Suggestions</h2>
        <p className="text-gray-600 mb-6">
          Upload an existing Midjourney or other AI-generated image and get typography layout
          suggestions for adding your quote text.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Image
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                imagePreview
                  ? "border-green-300 bg-green-50"
                  : "border-gray-300 hover:border-primary-500"
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              {imagePreview ? (
                <div>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded"
                  />
                  <p className="mt-2 text-sm text-green-600">
                    Click to change image
                  </p>
                </div>
              ) : (
                <div>
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-400">
                    PNG, JPG, WebP up to 10MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quote Input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quote Text
              </label>
              <textarea
                value={quoteText}
                onChange={(e) => setQuoteText(e.target.value)}
                placeholder="Enter the quote you want to add..."
                className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Author (optional)
              </label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Author name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing || !imageBase64 || !quoteText.trim()}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isAnalyzing ? "Analyzing..." : "Get Typography Suggestions"}
          </button>

          {(imagePreview || quoteText || result) && (
            <button
              onClick={clearAll}
              className="px-6 py-3 text-gray-600 hover:text-gray-800"
            >
              Clear All
            </button>
          )}
        </div>
      </section>

      {/* Results Section */}
      {result && (
        <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Typography Suggestions</h2>
            {imagePreview && quoteText && (
              <button
                onClick={() => setShowEditor(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
              >
                Open Editor
              </button>
            )}
          </div>

          {result.suggestions.length > 0 ? (
            <div className="space-y-6">
              {result.suggestions.map((suggestion, index) => (
                <SuggestionCard
                  key={index}
                  suggestion={suggestion}
                  index={index}
                />
              ))}
            </div>
          ) : result.rawAnalysis ? (
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {result.rawAnalysis}
              </p>
            </div>
          ) : (
            <p className="text-gray-500">No suggestions generated</p>
          )}
        </section>
      )}

      {/* Typography Editor Modal */}
      {showEditor && imagePreview && (
        <TypographyEditor
          imageDataUrl={imagePreview}
          quoteText={quoteText}
          author={author || undefined}
          suggestions={result?.suggestions || []}
          onClose={() => setShowEditor(false)}
        />
      )}
    </div>
  );
}

interface SuggestionCardProps {
  suggestion: TypographySuggestion;
  index: number;
}

function SuggestionCard({ suggestion, index }: SuggestionCardProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h3 className="font-medium text-gray-900 mb-3">
        Option {index + 1}: {suggestion.layout}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <span className="font-medium text-gray-700">Font:</span>
          <p className="text-gray-600">{suggestion.fontRecommendation}</p>
        </div>

        <div>
          <span className="font-medium text-gray-700">Colors:</span>
          <p className="text-gray-600">{suggestion.colorScheme}</p>
        </div>

        <div className="md:col-span-2">
          <span className="font-medium text-gray-700">Positioning:</span>
          <p className="text-gray-600">{suggestion.positioning}</p>
        </div>

        <div className="md:col-span-2">
          <span className="font-medium text-gray-700">Why it works:</span>
          <p className="text-gray-600">{suggestion.reasoning}</p>
        </div>
      </div>
    </div>
  );
}
